/**
 * Базовые стили для всех Input компонентов
 * Единый source of truth для стилизации форм
 */
import { styled } from '@mui/material/styles';
import { TextField, Select, Checkbox } from '@mui/material';

// CSS переменные для переиспользования размеров
const INPUT_PADDING = '1rem 1.25rem';

/**
 * Стандартный TextField - используется для большинства текстовых полей
 *
 * Typography standards:
 * - Input value: subtitle1 (1.5rem / 24px) + fontWeight 500
 * - Label: subtitle1 (1.5rem / 24px) + fontWeight 600
 */
export const StyledTextField = styled(TextField)(({ theme }) => ({
    '&.MuiTextField-root': {
        width: '100%',
        // CSS переменные для наследования в кастомных компонентах
        '--input-padding': INPUT_PADDING,
        '--input-font-size': theme.typography.subtitle1.fontSize,
        '--input-color': theme.palette.text.primary,
        '--input-color-secondary': theme.palette.text.secondary,
        '.MuiFormLabel-root': {
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: 600,
        },
        '.MuiInputBase-input': {
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: 500,
            padding: INPUT_PADDING,
        },
        '.MuiInputBase-root': {
            fontSize: theme.typography.subtitle1.fontSize,
        },
        '.MuiInputLabel-outlined': {
            transform: 'translate(14px, 1rem) scale(1)',
            '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.85)',
                fontWeight: 600,
            },
        },
    },
}));

/**
 * TextField для многострочного ввода (textarea)
 *
 * Typography standards:
 * - Input value: subtitle1 (1.5rem / 24px) + fontWeight 500
 * - Label: subtitle1 (1.5rem / 24px) + fontWeight 600
 */
export const StyledTextarea = styled(TextField)(({ theme }) => ({
    lineHeight: 1.4,
    '&.MuiTextField-root': {
        height: '100%',
        width: '100%',
        '.MuiInputBase-root': {
            height: '100%',
            alignItems: 'flex-start',
        },
        '.MuiFormLabel-root': {
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: 600,
        },
        '.MuiInputBase-input': {
            lineHeight: 1.4,
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: 500,
        },
        '.MuiInputLabel-outlined': {
            transform: 'translate(14px, 1rem) scale(1)',
            '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.85)',
                fontWeight: 600,
            },
        },
    },
}));

/**
 * Select с полной шириной и label
 *
 * Typography standards:
 * - Select value: subtitle1 (1.5rem / 24px) + fontWeight 500
 * - Label: subtitle1 (1.5rem / 24px) + fontWeight 600
 */
export const StyledSelect = styled(Select)(({ theme }) => ({
    '&.MuiInputBase-root': {
        fontSize: theme.typography.subtitle1.fontSize,
        fontWeight: 500,
        '.MuiSelect-select': {
            padding: '1rem 1.25rem',
            textAlign: 'left',
        },
        '.Mui-disabled': {
            WebkitTextFillColor: 'unset',
        },
    },
}));

/**
 * Стили для FormControl с Select - центрирование label
 * fontSize задается inline через theme в SelectInput.js
 */
export const selectFormControlSx = (theme) => ({
    '.MuiFormLabel-root': {
        fontSize: theme.typography.subtitle1.fontSize,
        fontWeight: 600,
    },
    '.MuiInputLabel-outlined': {
        transform: 'translate(14px, 1rem) scale(1)',
        '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.85)',
            fontWeight: 600,
        },
    },
});

/**
 * Компактный Select без бордера (для таблиц, inline редактирования)
 */
export const StyledSelectMinimal = styled(Select)(({ theme }) => ({
    '&.MuiInputBase-root': {
        fontSize: theme.typography.subtitle1.fontSize,
        fontWeight: 500,
        '.MuiSelect-select': {
            padding: 'unset !important',
            textAlign: 'center',
        },
        '.MuiSvgIcon-root': {
            display: 'none',
        },
        '.MuiOutlinedInput-notchedOutline': {
            border: 'unset',
        },
        '.Mui-disabled': {
            WebkitTextFillColor: 'unset',
        },
    },
}));

/**
 * Стилизованный Checkbox
 */
export const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
    '& .MuiSvgIcon-root': {
        fontSize: 24,
    },
    color: theme.palette.text.primary,
    '&.Mui-checked': {
        color: theme.palette.primary.main,
    },
}));

/**
 * Константы для MenuItem - размер наследуется от Select
 */
export const menuItemSx = {
    fontSize: 'inherit',
};

/**
 * Стили для TimeInput - использует CSS переменные от StyledTextField
 * Typography: body1 (20px) + fontWeight 500
 */
export const timeInputSx = {
    display: 'flex',
    alignItems: 'center',
    padding: 'var(--input-padding)',
    fontSize: 'var(--input-font-size)',
    fontWeight: 500,
    color: 'var(--input-color)',
    width: '100%',
    boxSizing: 'border-box',
};

export const timeInputSeparatorSx = {
    color: 'var(--input-color-secondary)',
    fontSize: 'inherit',
    fontWeight: 'inherit',
};

/**
 * Стили для TimeSelect внутри TimeInput
 */
export const timeSelectStyle = {
    border: 'none',
    background: 'transparent',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    fontFamily: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    outline: 'none',
    textAlign: 'center',
    textAlignLast: 'center',
    minWidth: 45,
    padding: '0 4px',
};
